
import { useAuthService } from './authService';
import api from './api';
import { store } from '../redux/store';
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
//import { setErrorMessage } from '../redux/errorSlice';
//import { store } from '../redux/store';
import { useLogout } from '../hooks/useLogout';
import ToastMsg from '../components/common/ToastMsg';
import { useAppSelector } from '../hooks/useTypedSelector';

let is409ErrorActive = false;
export function getIs409ErrorActive() {
  return is409ErrorActive;
}

export const useApi = () => {
  const { isAuthenticated, getToken } = useAuthService();
  const interceptorsAdded = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();
  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isModalOpen = useAppSelector((state: any) => state.mobileVerificationData.isModalOpen);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);

  useEffect(() => {
    if (isAuthenticated && !interceptorsAdded.current) {
      api.interceptors.request.use(
        async (config) => {

          try {
            const state = store.getState();
            const mobileUnverified = state?.mobileVerificationData?.mobileUnverifiedUser;
            const url = (config?.url || '').toString();
            const blockedFragments = [
              'shortlist',
              '/shortlist',
              'similar-colleges',
              'similar-courses',
              'recommend',
              'recently-viewed',
              'trending',
            ];
            const lowUrl = url.toLowerCase();

            const verificationJustCompleted = sessionStorage.getItem('mobile_verification_completed') === 'true';

            const shouldBlock = (isFreeUser || mobileUnverified) && blockedFragments.some(f => lowUrl.includes(f.toLowerCase())) && !verificationJustCompleted;
            if (shouldBlock) {
              console.log('Blocking request due to mobile unverified user:', url);
              let syntheticBody: any = { data: [] };
              if (lowUrl.includes('college-list')) {
                (syntheticBody as any) = { data: [], total: 0 };
              } else if (lowUrl.includes('shortlist')) {
                (syntheticBody as any) = { data: [] };
              } else if (lowUrl.includes('similar-colleges')) {
                (syntheticBody as any) = { data: [] };
              } else if (lowUrl.includes('recommend')) {
                (syntheticBody as any) = { courseByCollegeIds: [], collegeIds: [], data: [] };
              } else if (lowUrl.includes('recently-viewed')) {
                (syntheticBody as any) = { data: [] };
              } else if (lowUrl.includes('coursebycollege-list') || lowUrl.includes('course-by-college')) {
                (syntheticBody as any) = { data: [], total: 0 };
              }

              const syntheticResponse = {
                data: syntheticBody,
                status: 200,
                statusText: 'OK',
                headers: {},
                config,
              } as any;

              config.adapter = () => Promise.resolve(syntheticResponse);
              return config;
            }

            if (verificationJustCompleted) {
              sessionStorage.removeItem('mobile_verification_completed');
            }
          } catch (ex) {
            console.warn('Could not evaluate mobile-unverified guard', ex);
          }
          try {
            // Check if user is logging out
            if (sessionStorage.getItem('is_logging_out') === 'true') {
              return config;
            }
            const { access_token } = await getToken();
            if (access_token) {
              config.headers.Authorization = `Bearer ${access_token}`;
            }
            return config;
          } catch (error) {
            const e: any = error;
            const status = e?.response?.status || e?.status;
            const errMessage = (e?.response?.data?.message || e?.message || '').toString();

            // If the refresh token is invalid/unknown treat it as session expired so
            // we don't keep retrying and so the user is redirected to session-expired
            if (
              (errMessage.toLowerCase().includes('unknown') && errMessage.toLowerCase().includes('refresh token')) ||
              errMessage.toLowerCase().includes('invalid refresh token') ||
              errMessage.toLowerCase().includes('invalid refresh') ||
              errMessage.toLowerCase().includes('user not found') ||
              errMessage.toLowerCase().includes('jwt expired')
            ) {
              try {
                try {
                  sessionStorage.setItem('session_expired', 'true');
                } catch (ssErr) {
                  console.error('Failed to set sessionStorage flag for session_expired:', ssErr);
                }
                // Do NOT call logout() here — we only want to show the session-expired
                // page with a logout button so the user can decide to log out.
              } catch (logoutErr) {
                console.error('Error while setting session-expired state:', logoutErr);
              }
              try {
                navigate('/session-expired');
              } catch (error_) {
                console.error('Navigation to session-expired failed:', error_);
              }

              const sessionErr: any = new Error('Session expired');
              sessionErr.response = { status: 403, data: { message: errMessage || 'Session expired' } };
              sessionErr.noRetry = true;
              sessionErr.isSessionExpired = true;
              sessionErr._skipToast = true;
              return Promise.reject(sessionErr);
            }

            if (status === 403) {
              try {
                // Mark session as expired in sessionStorage so other parts of the app
                // can detect the event and avoid retrying requests.
                try {
                  sessionStorage.setItem('session_expired', 'true');
                } catch (ssErr) {
                  console.error('Failed to set sessionStorage flag for session_expired:', ssErr);
                }

                // Do NOT call logout() immediately. Instead show the session-expired
                // page which contains a logout button so the user can initiate logout.
              } catch (logoutErr) {
                console.error('Error while setting session-expired state:', logoutErr);
              }
              // As a fallback, navigate to the session-expired route in case logout didn't redirect
              try {
                navigate('/session-expired');
              } catch (error_) {
                console.error('Navigation to session-expired failed:', error_);
              }

              // Create an error object with a response.status so callers that inspect
              // `err.response?.status` will correctly detect 403 and avoid retries.
              const sessionErr: any = new Error('Session expired');
              sessionErr.response = { status: 403, data: { message: 'Session expired' } };
              // Mark it so any retry logic can opt-out quickly
              sessionErr.noRetry = true;
              sessionErr.isSessionExpired = true;
              sessionErr._skipToast = true;

              return Promise.reject(sessionErr);
            }

            const err = error as Error;
            return Promise.reject(err instanceof Error ? err : new Error('Unknown request error'));
          }
        },
        (error) => {
          if (sessionStorage.getItem('is_logging_out') !== 'true') {
            console.error('Request Interceptor Error:', error);
          }
          return Promise.reject(error instanceof Error ? error : new Error(error?.message || 'Unknown error'));
        }
      );

      const handle401 = (navigate: Function) => {
        navigate('/login');
      };

      const handle402 = (navigate: Function) => {
        if (
          location.pathname === "/mylist-college" ||
          location.pathname === "/mylist-course" ||
          location.pathname === "/recommend-college-dream" ||
          location.pathname === "/recommend-college-safe" ||
          location.pathname === "/recommend-college-target" ||
          location.pathname === "/recommend-course" ||
          location.pathname === "/college" ||
          location.pathname === "/course"
        ) {
          ToastMsg("warning", "Please upgrade your plan to view the details.");
        } else {
          navigate("/upgrade");
        }
      }
      const handle403 = (message: string, navigate: Function, location: any) => {
        if (message === "Email Not Verified") {
          navigate('/verify-email');
        } else if (message === "Phone Number Not Verified") {
          if (location.pathname !== '/mobile-verification') {
            navigate('/mobile-verification');
          } else {
            return true;
          }
        }
        return false;
      };

      const handle409 = (message: string, navigate: Function) => {
        if (is409ErrorActive) {
          return;
        }
        is409ErrorActive = true;
        const currentPath = window.location.pathname;
        const isOnMobileVerificationPage = currentPath.includes("mobile-verification");
        if (isOnMobileVerificationPage) {
          console.log("On mobile verification page. Showing toast only, no navigation.");
          return;
        }
        if (
          message?.toLowerCase().includes('sign-in method') ||
          message?.toLowerCase().includes('login using')
        ) {
          sessionStorage.setItem('mismatched_login_error', message || '');
          window.location.replace('/error-mismatched-login');
        }
      };

      const handle404 = (message: string, navigate: any) => {
        const currentPath = window.location.pathname.toLowerCase();

        const skipPrefixes = [
          "/mylist-college",
          "/compare-college",
          "/compare-course"
        ];

        const isHome = currentPath === "/";

        const shouldSkip =
          isHome ||
          skipPrefixes.some(prefix => currentPath.startsWith(prefix));

        const isErrorMessage =
          message?.toLowerCase().includes("not found") ||
          message?.toLowerCase().includes("no record found");

        console.log({
          currentPath,
          isHome,
          shouldSkip,
          isErrorMessage
        });

        if (isErrorMessage && !shouldSkip) {
          navigate("/error-page-not-found", { replace: true });
        }
      };


      const handle429 = (
        message: string,
        location: any,
        navigate: Function
      ) => {
        const currentPath = window.location.pathname;
        const isOnCompareCollegePage = currentPath.includes("compare-college");
        const isOnCompareCoursePage = currentPath.includes("compare-course");
        const isOnMobileVerificationPage = currentPath.includes("mobile-verification")
        const isOnOnboardPage = currentPath.includes("onboard");
        const isOnMyProfilePage = currentPath.includes("my-profile");

        console.log("429 Triggered:");
        console.log("Message:", message);
        console.log("Current Path:", currentPath);
        console.log("Is On Compare College Page:", isOnCompareCollegePage);
        console.log("Is On Compare Course Page:", isOnCompareCoursePage);

        if (isOnCompareCollegePage || isOnCompareCoursePage) {
          console.log("You're already on a compare page. No navigation happening here.");
          ToastMsg("warning", message || "You've hit the limit.");
          return;
        }

        if (isOnMobileVerificationPage) {
          console.log("On mobile verification page. Showing toast only, no navigation.");
          return;
        }

        if (isOnOnboardPage) {
          console.log("On onboard page. Showing toast only, no navigation.");
          return;
        }

        if (isOnMyProfilePage) {
          console.log("On my profile page. Showing toast only, no navigation.");
          return;
        }

        if (isModalOpen) {
          console.log("Mobile verification modal open. Skipping navigation.");
          return;
        }

        if (currentPath.includes("course") && message.toLowerCase().includes('API usage limit exceeded for this endpoint.')) {
          console.log("Redirecting to /error-course-limit-exceeded...");
          setTimeout(() => {
            if (mobileUnverifiedUser) {
              navigate("/error-course-limit-exceeded-mobile-unverified")
            }
            else if (isFreeUser === true) {
              navigate("/error-course-limit-exceeded-upgrade");
            }
            else {
              navigate("/error-course-limit-exceeded");
            }
          }, 0);
        } else {
          console.log("Redirecting to /error-college-limit-exceeded...");
          setTimeout(() => {
            if (mobileUnverifiedUser === true) {
              navigate("/error-college-limit-exceeded-mobile-unverified")
            }
            else if (isFreeUser === true) {
              navigate("/error-college-limit-exceeded-upgrade");
            }
            else {
              navigate("/error-college-limit-exceeded");
            }

          }, 0);
        }
      };

      const handle503 = (message: string, logout: Function) => {
        if (message?.toLowerCase().includes("Server Under Maintenance, Please Try Again Later")) {
          setTimeout(() => {
            navigate('/error-server-maintenance');
          })
        }
      }

      const handle500 = (message: string, logout: Function, navigate: Function) => {
        if (message?.toLowerCase().includes('internal server error')) {
          setTimeout(() => {
            navigate('/internal-server-error');
          }, 0);
        }
      };

      const responseErrorHandler = (
        error: any,
        navigate: Function,
        logout: Function,
        location: any
      ) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.message;
        const lowMsg = (message || '')?.toString()?.toLowerCase();

        // If this error was already handled as session-expired in the request interceptor, skip further processing
        if (error?.isSessionExpired) {
          return Promise.reject(error);
        }

        if (sessionStorage.getItem('is_logging_out') === 'true') {
          error._skipToast = true;
          return Promise.reject(error);
        }

        // If server returns a 500 (or any) with an "unknown/invalid refresh token" message
        // treat it as session expired so the app shows the session-expired page instead
        // of navigating to the internal-server-error page.
        if (
          (lowMsg?.includes('unknown') && lowMsg?.includes('refresh token')) ||
          lowMsg?.includes('invalid refresh token') ||
          lowMsg?.includes('invalid refresh') ||
          lowMsg?.includes('user not found') ||
          lowMsg?.includes('jwt expired')
        ) {
          try {
            try {
              sessionStorage.setItem('session_expired', 'true');
            } catch (ssErr) {
              console.error('Failed to set sessionStorage flag for session_expired:', ssErr);
            }
            // Do NOT auto-logout; just show the session-expired page.
            navigate('/session-expired');
          } catch (navErr) {
            console.error('Navigation to session-expired failed:', navErr);
          }

          const sessionErr: any = new Error('Session expired');
          sessionErr.response = { status: 403, data: { message } };
          sessionErr.noRetry = true;
          sessionErr.isSessionExpired = true;
          sessionErr._skipToast = true;
          return Promise.reject(sessionErr);
        }

        // Allow handling of errors even if a 409 previously occurred. We keep
        // `is409ErrorActive` to avoid duplicate side-effects (like navigation)
        // when multiple 409 responses arrive in quick succession, but we should
        if (sessionStorage.getItem('is_logging_out') !== 'true') {
          console.error('Response Interceptor Error:', {
            status,
            url: error.config?.url,
            method: error.config?.method,
            message: error.message,
          });
        }

        switch (status) {
          case 401:
            handle401(navigate);
            break;
          case 402:
            handle402(navigate);
            break;
          case 403: {
            const bypass = handle403(message, navigate, location);
            if (bypass) return Promise.resolve(error.response);
            // Return early for 403 to prevent it from reaching other handlers
            return Promise.reject(error instanceof Error ? error : new Error(error?.message || 'Unknown error'));
          }
          case 409:
            handle409(message, navigate);
            error.noRetry = true;
            break;
          case 404:
            handle404(message, navigate);
            break;
          case 429:
            handle429(message, location, navigate);
            error.noRetry = true;
            break;
          case 500:
            handle500(message, logout, navigate);
            break;
          case 503:
            handle503(message, logout);
            break;
        }
        return Promise.reject(error instanceof Error ? error : new Error(error?.message || 'Unknown error'));
      };


      api.interceptors.response.use(
        (response) => {
          if (is409ErrorActive) is409ErrorActive = false;
          return response;
        },
        (error) => responseErrorHandler(error, navigate, logout, location)
      );

      interceptorsAdded.current = true;
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getToken, navigate, logout, location, mobileUnverifiedUser]);

  return api;
};

export default api;